import React, { useEffect, useState, useRef } from 'react';
import axios from 'axios';
import { Box, Typography, Button, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Paper, TextField, Dialog, DialogTitle, DialogContent, DialogActions, Switch, IconButton, InputAdornment, FormControlLabel } from '@mui/material';
import PaginacionControl from '../components/PaginacionControl';
import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import FileDownloadIcon from '@mui/icons-material/GetApp';
import CloseIcon from '@mui/icons-material/Close';
import SaveIcon from '@mui/icons-material/Save';
import SearchIcon from '@mui/icons-material/Search';
import { GridOn } from '@mui/icons-material';

const initialForm = {
  codigo: '',
  descripcion: '',
  longitud: '',
  activo: true
};

export default function TipoDocumentoIdentidad() {
  const [tipos, setTipos] = useState([]);
  const [filtered, setFiltered] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(initialForm);
  const [editing, setEditing] = useState(null);
  const [search, setSearch] = useState('');
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(8);

  useEffect(() => {
    fetchTipos();
  }, []);

  useEffect(() => {
    if (!search) {
      setFiltered(tipos);
    } else {
      setFiltered(
        tipos.filter(tipo =>
          (tipo.codigo || '').toLowerCase().includes(search.toLowerCase()) ||
          (tipo.descripcion || '').toLowerCase().includes(search.toLowerCase())
        )
      );
    }
    setPage(0);
  }, [search, tipos]);

  const fetchTipos = async () => {
    const res = await axios.get('/api/tipo-documento-identidad');
    setTipos(res.data);
    setFiltered(res.data);
  };

  const handleOpen = (tipo = null) => {
    setEditing(tipo);
    setForm(tipo ? { ...tipo } : initialForm);
    setOpen(true);
  };

  const handleClose = () => {
    setOpen(false);
    setEditing(null);
  };

  const handleChange = e => {
    const { name, value, type, checked } = e.target;
    setForm({ ...form, [name]: type === 'checkbox' ? checked : value });
  };

  const handleSave = async () => {
    if (editing) {
      await axios.put(`/api/tipo-documento-identidad/${editing.id}`, form);
    } else {
      await axios.post('/api/tipo-documento-identidad', form);
    }
    fetchTipos();
    setOpen(false);
  };

  const handleDelete = async id => {
    await axios.delete(`/api/tipo-documento-identidad/${id}`);
    fetchTipos();
  };

  const handleActive = async (tipo, activo) => {
    await axios.put(`/api/tipo-documento-identidad/${tipo.id}`, { ...tipo, activo });
    fetchTipos();
  };

  const exportExcel = async () => {
    const { exportGenericWorkbook } = await import('../utils/excel/genericWorkbook');
    const rows = filtered.map(tipo => ({
      id: tipo.id,
      codigo: tipo.codigo,
      descripcion: tipo.descripcion,
      longitud: tipo.longitud,
      estadoStr: tipo.activo ? 'ACTIVO' : 'INACTIVO'
    }));
    await exportGenericWorkbook({
      titulo: 'REPORTE - TIPOS DE DOCUMENTO IDENTIDAD',
      sheetName: 'TipoDocIdentidad',
      tableName: 'tblTipoDocIdentidad',
      fileName: 'TipoDocIdentidad',
      columns: [
        { key: 'id', label: 'ID', width: 8 },
        { key: 'codigo', label: 'Código', width: 14 },
        { key: 'descripcion', label: 'Descripción', width: 30 },
        { key: 'longitud', label: 'Longitud', width: 14 },
        { key: 'estadoStr', label: 'Estado', width: 14, isEstado: true }
      ],
      rows,
      searchText: search,
      usuario: ''
    });
  };

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', mt: 1, mb: 4, width: '100%' }}>
      <Box sx={{ width: { xs: '100%', sm: '95%', md: '90%' }, maxWidth: 1800 }}>
        <Typography variant="h4" gutterBottom sx={{ textAlign: { xs: 'center', md: 'left' }, mb: 3, fontWeight: 'bold', color: '#222' }}>Gestión de Tipos de Documento de Identidad</Typography>
        <Box sx={{ display: 'flex', flexDirection: { xs: 'column', sm: 'row' }, gap: 2, mb: 2 }}>
          <TextField
            placeholder="Buscar..."
            value={search}
            onChange={e => setSearch(e.target.value)}
            size="small"
            InputProps={{ startAdornment: <Box sx={{ color: 'text.secondary', mr: 1 }}><SearchIcon /></Box> }}
            sx={{ flex: 1 }}
          />
          <Button
            variant="contained"
            color="success"
            startIcon={<GridOn />}
            onClick={exportExcel}
            sx={{ minWidth: 120 }}
          >Exportar Excel</Button>
          <Button variant="contained" startIcon={<AddIcon />} onClick={() => handleOpen()} sx={{ ml: { xs: 0, sm: 'auto' }, mt: { xs: 2, sm: 0 } }}>
            Nuevo Tipo
          </Button>
          <TextField
            size="small"
            label="Mostrar"
            type="number"
            value={rowsPerPage}
            onChange={e => { setRowsPerPage(Number(e.target.value)); setPage(0); }}
            sx={{ width: 100 }}
          />
        </Box>
        <TableContainer component={Paper} sx={{ borderRadius: 4, boxShadow: '0 4px 24px rgba(0,0,0,0.08)', mt: 2, overflowX: 'auto' }}>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell align="center">Código</TableCell>
                <TableCell align="center">Descripción</TableCell>
                <TableCell align="center">Longitud</TableCell>
                <TableCell align="center">Estado</TableCell>
                <TableCell align="center">Acciones</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {filtered
                .slice(rowsPerPage === -1 ? 0 : page * rowsPerPage, rowsPerPage === -1 ? undefined : (page + 1) * rowsPerPage)
                .map(tipo => (
                  <TableRow key={tipo.id}>
                    <TableCell align="center">{tipo.codigo}</TableCell>
                    <TableCell align="center">{tipo.descripcion}</TableCell>
                    <TableCell align="center">{tipo.longitud}</TableCell>
                    <TableCell align="center">
                      <Switch checked={!!tipo.activo} color="success" onChange={e => handleActive(tipo, e.target.checked)} />
                    </TableCell>
                    <TableCell align="center">
                      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', gap: 1 }}>
                        <IconButton color="primary" onClick={() => handleOpen(tipo)} size="small"><EditIcon /></IconButton>
                        <IconButton color="error" onClick={() => handleDelete(tipo.id)} size="small"><DeleteIcon /></IconButton>
                      </Box>
                    </TableCell>
                  </TableRow>
                ))}
            </TableBody>
          </Table>
        </TableContainer>
        <PaginacionControl page={page} rowsPerPage={rowsPerPage} count={filtered.length} onPageChange={setPage} />
        <Dialog open={open} onClose={(_, r) => r !== 'backdropClick' && handleClose()} disableEscapeKeyDown={false} maxWidth="sm" fullWidth>
          <DialogTitle>
            <Typography variant="h6" fontWeight="bold">
              {editing ? 'Editar' : 'Nuevo'} Tipo de Documento de Identidad
            </Typography>
          </DialogTitle>
          <DialogContent>
            <Box sx={{ display: 'flex', flexDirection: { xs: 'column', sm: 'row' }, gap: 2 }}>
              <TextField margin="dense" label="Código" name="codigo" value={form.codigo} onChange={handleChange} fullWidth required sx={{ flex: 2 }} />
              <TextField margin="dense" label="Descripción" name="descripcion" value={form.descripcion} onChange={handleChange} fullWidth required sx={{ flex: 6 }} />
              <TextField margin="dense" label="Longitud" name="longitud" value={form.longitud} onChange={handleChange} type="number" fullWidth required sx={{ flex: 2 }} />
            </Box>
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
              <Typography variant="body1" sx={{ mr: 1 }}>Estado:</Typography>
              <Switch checked={!!form.activo} onChange={handleChange} name="activo" color="success" />
              <Typography variant="body2" color={form.activo ? 'success.main' : 'text.secondary'}>
                {form.activo ? 'Activo' : 'Inactivo'}
              </Typography>
            </Box>
          </DialogContent>
          <DialogActions>
            <Button onClick={handleClose} color="error" variant="contained" startIcon={<CloseIcon />}>
              Cancelar
            </Button>
            <Button onClick={handleSave} variant="contained" color="primary" startIcon={<SaveIcon />}>
              Guardar
            </Button>
          </DialogActions>
        </Dialog>
      </Box>
    </Box>
  );
}
